import Cookies from 'js-cookie'
import { Platform } from 'react-native'

/**
 * The first message a visitor typed in jinshuju.net's hero input, handed to `/jiri` in the URL fragment.
 *
 * Cross-repo contract with jinshuju.net (`utils/jiri-draft.ts`) and nextfe (`src/modules/jiri/jiriDraft.ts`): the
 * site sends every visitor to nextfe's `/jiri`, and nextfe forwards a phone here with the fragment intact.
 *
 * The fragment also carries a nonce the site writes to a cookie on the registrable domain at submit. Only the
 * browser that used the box has it, so a link someone else crafted cannot speak in the reader's voice.
 */
export type JiriUserMessage = {
  content: string
  attachments: {
    attachmentId: string
    fileName?: string | null
    fileSize?: number | null
    contentType?: string | null
    visitUrl?: string | null
    fileWidth?: number | null
    fileHeight?: number | null
  }[]
}

const HASH_PARAM = 'jiri_draft'

// One name for the nonce wherever it travels: the fragment and the cookie
const NONCE_PARAM = 'jiri_draft_nonce'

const STORAGE_KEY = '__next-app-jiri-draft'

const STASHED_AT_KEY = '__next-app-jiri-draft-stashed-at'

// Long enough for a signed-out visitor's trip through login
const STALE_AFTER_MS = 10 * 60 * 1000

// The site's box cannot exceed these, so a payload past either was not written there
const MAX_CONTENT_LENGTH = 2000
const MAX_ATTACHMENTS = 3

function decodeBase64Url(value: string): string {
  const binary = atob(value.replace(/-/g, '+').replace(/_/g, '/'))
  return new TextDecoder().decode(Uint8Array.from(binary, char => char.charCodeAt(0)))
}

function validateDraft(value: unknown): JiriUserMessage | null {
  const draft = value as Partial<JiriUserMessage> | null

  if (typeof draft?.content !== 'string' || !Array.isArray(draft.attachments)) return null
  if (draft.content.length > MAX_CONTENT_LENGTH || draft.attachments.length > MAX_ATTACHMENTS) return null

  const attachments = draft.attachments.filter(attachment => typeof attachment?.attachmentId === 'string')
  if (!draft.content.trim() && !attachments.length) return null

  return { content: draft.content, attachments }
}

function hashParams() {
  return new URLSearchParams(window.location.hash.slice(1))
}

function readHashDraft(): JiriUserMessage | null {
  const hash = hashParams()
  const encoded = hash.get(HASH_PARAM)
  const nonce = hash.get(NONCE_PARAM)
  if (!encoded || !nonce || nonce !== Cookies.get(NONCE_PARAM)) return null

  try {
    return validateDraft(JSON.parse(decodeBase64Url(encoded)))
  } catch {
    return null
  }
}

function readStoredDraft(): JiriUserMessage | null {
  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY)
    if (!stored) return null

    const stashedAt = Number(window.sessionStorage.getItem(STASHED_AT_KEY))
    if (!stashedAt || Date.now() - stashedAt > STALE_AFTER_MS) return null

    return validateDraft(JSON.parse(stored))
  } catch {
    return null
  }
}

export function hasJiriDraftInUrl(): boolean {
  return Platform.OS === 'web' && hashParams().has(HASH_PARAM)
}

/**
 * The draft this page load arrived with, or null. Safe to call during render.
 *
 * Storage is the fallback rather than the other way round: a draft in the fragment is always the newer one, and
 * once `stashJiriDraft` has spent the nonce, the fragment's copy is no longer readable at all.
 */
export function readJiriDraft(): JiriUserMessage | null {
  if (Platform.OS !== 'web') return null
  return readHashDraft() ?? readStoredDraft()
}

/**
 * Keep the draft in this tab's sessionStorage, which survives the trip through goldendata's login where the
 * fragment does not: the return address carries the path alone.
 */
export function stashJiriDraft() {
  if (!hasJiriDraftInUrl()) return

  const draft = readHashDraft()
  // Spent, whether or not it matched: one nonce buys one handoff. Removed on the domain the site set it on, as
  // removing a host-only cookie of the same name leaves that one standing.
  const labels = window.location.hostname.split('.')
  Cookies.remove(NONCE_PARAM, { path: '/', domain: labels.length > 2 ? `.${labels.slice(-2).join('.')}` : undefined })
  if (!draft) return

  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(draft))
    window.sessionStorage.setItem(STASHED_AT_KEY, String(Date.now()))
  } catch {
    // Storage unavailable; the draft is lost
  }
}

/** Once the draft has opened a conversation, so a reload never sends it again. */
export function clearJiriDraft() {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY)
    window.sessionStorage.removeItem(STASHED_AT_KEY)
  } catch {
    // Nothing was stored in the first place
  }
}
